// UpdateWizard.js
import { useEffect, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import useSensorUpdate from '../../../hooks/useSensorUpdate';
import { createSerialAdapter } from '../../../services/firmware/serialAdapter';
import { createEspWebToolsAdapter } from '../../../services/firmware/espWebToolsAdapter';
import { createFirmwareApi } from '../../../services/firmware/firmwareApi';
import { API_ENDPOINT } from '../../../services/common';
import GateStep from './GateStep';
import DetectStep from './DetectStep';
import DriversStep from './DriversStep';
import UpToDateStep from './UpToDateStep';
import ConfirmStep from './ConfirmStep';
import UpdatingStep from './UpdatingStep';
import DoneStep from './DoneStep';
import ErrorPanel from './ErrorPanel';

export default function UpdateWizard({ model = 'PTQS1005', onExit, deps }) {
  const installBtnRef = useRef(null);
  const resolvedDeps = useMemo(() => deps || {
    serial: createSerialAdapter(),
    espWebTools: createEspWebToolsAdapter(),
    firmwareApi: createFirmwareApi({ apiEndpoint: API_ENDPOINT }),
    nav: navigator,
  }, [deps]);

  const serialRef = useRef(resolvedDeps.serial);
  serialRef.current = resolvedDeps.serial;
  useEffect(() => () => { serialRef.current?.release?.().catch(() => {}); }, []);

  const { state, start, confirm, retry, cancel } = useSensorUpdate({ model, ...resolvedDeps });

  useEffect(() => { start(); }, [start]);

  const handleConfirm = () => {
    if (state.method === 'usb' && installBtnRef.current) {
      const btn = installBtnRef.current.querySelector('esp-web-install-button');
      if (btn) btn.click(); // user gesture opens the ESP Web Tools dialog
    }
    confirm();
  };

  const manifestUrl = state.toVersion
    ? `${API_ENDPOINT}/firmware/manifest?model=${model}&version=${state.toVersion}`
    : `${API_ENDPOINT}/firmware/manifest?model=${model}`;

  return (
    <Box sx={{ minHeight: 380 }}>
      {/* Hidden install button used only for the USB branch's user gesture */}
      <Box ref={installBtnRef} sx={{ display: state.method === 'usb' && state.step === 'confirm' ? 'flex' : 'none', justifyContent: 'center', mb: 1 }}>
        <esp-web-install-button manifest={manifestUrl} />
      </Box>

      {state.step === 'gate' && <DetectStep />}
      {state.step === 'blocked' && <GateStep onRetry={start} onCancel={onExit} />}
      {state.step === 'detecting' && <DetectStep />}
      {state.step === 'driversHelp' && <DriversStep onRetry={retry} onCancel={onExit} />}
      {state.step === 'upToDate' && <UpToDateStep version={state.fromVersion} onDone={onExit} />}
      {state.step === 'confirm' && <ConfirmStep method={state.method} toVersion={state.toVersion} onConfirm={handleConfirm} />}
      {state.step === 'updating' && <UpdatingStep progress={state.progress} method={state.method} />}
      {state.step === 'verifying' && <UpdatingStep progress={1} method={state.method} />}
      {state.step === 'done' && <DoneStep fromVersion={state.fromVersion} toVersion={state.toVersion} onDone={onExit} />}
      {state.step === 'error' && <ErrorPanel error={state.error} onRetry={retry} onCancel={() => { cancel(); onExit && onExit(); }} />}
    </Box>
  );
}
